import React, { useRef, useEffect } from 'react';

export default function ShareModal({ open, onClose, message = '', url = '' }) {
  const textareaRef = useRef(null);
  
  // Focus textarea when modal opens
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 100);
    }
  }, [open]);

  if (!open) return null;

  const finalUrl = url || window.location.href;
  
  // Verbesserter Share-Text mit VersusArena-Branding
  const enhancedMessage = message ? 
    `🎮 ${message}\n\n🚀 Join me in VersusArena - The ultimate gaming tournament platform!\n\n${finalUrl}` :
    `🎮 Check out this amazing tournament on VersusArena!\n\n🚀 Join me in VersusArena - The ultimate gaming tournament platform!\n\n${finalUrl}`;

  const enc = encodeURIComponent;

  // Auto-select text when textarea is clicked
  const handleTextareaClick = () => {
    if (textareaRef.current) {
      textareaRef.current.select();
      textareaRef.current.setSelectionRange(0, 99999); // For mobile devices
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(enhancedMessage);
      // Besseres Feedback als alert
      const button = document.activeElement;
      const originalText = button.textContent;
      button.textContent = '✓ Copied!';
      button.style.background = 'linear-gradient(135deg, #00ff88 0%, #00d4aa 100%)';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
      }, 2000);
    } catch (err) {
      alert('Copied to clipboard!');
    }
  };

  const shareX = () =>
    window.open(`https://twitter.com/intent/tweet?text=${enc(message || 'Check out this tournament!')}&url=${enc(finalUrl)}&hashtags=VersusArena,Gaming,Tournament`, '_blank');
  
  const shareWhatsApp = () =>
    window.open(`https://api.whatsapp.com/send?text=${enc(enhancedMessage)}`, '_blank');
  
  const shareTelegram = () =>
    window.open(`https://t.me/share/url?url=${enc(finalUrl)}&text=${enc(message || 'Check out this tournament on VersusArena!')}`, '_blank');
  
  const shareSMS = () =>
    window.open(`sms:?&body=${enc(enhancedMessage)}`, '_self');
  
  const shareMore = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ 
          title: 'VersusArena Tournament', 
          text: message || 'Check out this tournament!', 
          url: finalUrl 
        });
      } else {
        await copyText();
      }
    } catch {}
  };

  return (
    <>
      {/* Inline CSS für VersusArena Share Modal */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { 
            opacity: 0; 
            transform: scale(0.9) translateY(-20px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 15px rgba(255, 140, 0, 0.4); }
          50% { box-shadow: 0 6px 25px rgba(255, 140, 0, 0.6); }
        }

        .share-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.85);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000000;
          animation: fadeIn 0.3s ease-out;
        }

        .share-modal {
          width: min(580px, 95vw);
          max-height: 90vh;
          overflow-y: auto;
          background: linear-gradient(135deg, #0a0e1a 0%, #1a1a2e 50%, #0f0f1b 100%);
          color: #fff;
          border-radius: 20px;
          border: 1px solid rgba(255, 140, 0, 0.2);
          box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,140,0,0.1);
          overflow: hidden;
          position: relative;
          animation: slideIn 0.3s ease-out;
        }

        .share-modal-header {
          background: linear-gradient(135deg, #ff8c00 0%, #ff6347 100%);
          padding: 24px 28px 20px;
          border-bottom: 1px solid rgba(255,140,0,0.3);
          position: relative;
        }

        .share-modal-title { 
          margin: 0; 
          font-size: 24px; 
          font-weight: 800;
          text-shadow: 0 2px 8px rgba(0,0,0,0.3);
          letter-spacing: 0.5px;
          position: relative;
          z-index: 1;
        }

        .share-modal-subtitle { 
          margin: 8px 0 0 0; 
          font-size: 14px;
          opacity: 0.9;
          font-weight: 500;
          position: relative;
          z-index: 1;
        }

        .share-modal-textarea-container {
          padding: 24px 28px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .share-modal-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #ff8c00;
          margin-bottom: 12px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .share-modal-textarea {
          width: 100%;
          min-height: 110px;
          max-height: 200px;
          resize: vertical;
          background: rgba(15, 15, 25, 0.8);
          color: #fff;
          border: 2px solid rgba(255,140,0,0.2);
          border-radius: 12px;
          padding: 16px;
          outline: none;
          font-size: 14px;
          line-height: 1.5;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          box-sizing: border-box;
        }

        .share-modal-textarea:hover {
          border-color: rgba(255,140,0,0.4);
          background: rgba(15, 15, 25, 0.9);
        }

        .share-modal-textarea:focus {
          border-color: #ff8c00;
          box-shadow: 0 0 0 3px rgba(255, 140, 0, 0.2);
          background: rgba(15, 15, 25, 1);
        }

        .share-modal-textarea-hint {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          margin-top: 8px;
          text-align: center;
          font-style: italic;
        }

        .share-modal-buttons-container {
          padding: 24px 28px;
        }

        .share-modal-button-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
        }

        .share-modal-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 48px;
          padding: 0 16px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          text-transform: none;
          box-sizing: border-box;
        }

        .share-modal-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.1);
        }

        .share-modal-btn:active {
          transform: translateY(0);
          transition: transform 0.1s ease;
        }

        .share-modal-btn-icon {
          font-size: 16px;
          line-height: 1;
        }

        .share-modal-btn-text {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.3px;
        }

        .share-modal-btn-copy {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
        }

        .share-modal-btn-copy:hover {
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
        }

        .share-modal-btn-x {
          background: linear-gradient(135deg, #1da1f2 0%, #0d8bd9 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(29, 161, 242, 0.3);
        }

        .share-modal-btn-x:hover {
          box-shadow: 0 6px 20px rgba(29, 161, 242, 0.5);
        }

        .share-modal-btn-whatsapp {
          background: linear-gradient(135deg, #25d366 0%, #1ebe57 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
        }

        .share-modal-btn-whatsapp:hover {
          box-shadow: 0 6px 20px rgba(37, 211, 102, 0.5);
        }

        .share-modal-btn-telegram {
          background: linear-gradient(135deg, #0088cc 0%, #006bb3 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(0, 136, 204, 0.3);
        }

        .share-modal-btn-telegram:hover {
          box-shadow: 0 6px 20px rgba(0, 136, 204, 0.5);
        }

        .share-modal-btn-sms {
          background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(155, 89, 182, 0.3);
        }

        .share-modal-btn-sms:hover {
          box-shadow: 0 6px 20px rgba(155, 89, 182, 0.5);
        }

        .share-modal-btn-more {
          background: linear-gradient(135deg, #ff8c00 0%, #ff6347 100%);
          color: #fff;
          box-shadow: 0 4px 15px rgba(255, 140, 0, 0.4);
          animation: pulse 2s infinite;
        }

        .share-modal-btn-more:hover {
          box-shadow: 0 6px 25px rgba(255, 140, 0, 0.6);
          animation: none;
        }

        .share-modal-footer {
          padding: 16px 28px 24px;
          display: flex;
          justify-content: center;
        }

        .share-modal-btn-close {
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.8);
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .share-modal-btn-close:hover {
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.2);
          color: #fff;
        }

        @media (max-width: 768px) {
          .share-modal {
            width: 95vw;
            margin: 10px;
          }
          
          .share-modal-button-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          
          .share-modal-header,
          .share-modal-textarea-container,
          .share-modal-buttons-container {
            padding-left: 20px;
            padding-right: 20px;
          }
          
          .share-modal-title {
            font-size: 20px;
          }
          
          .share-modal-btn {
            height: 44px;
            font-size: 13px;
          }
          
          .share-modal-btn-text {
            font-size: 12px;
          }
        }

        @media (max-width: 480px) {
          .share-modal {
            width: 98vw;
            margin: 5px;
            border-radius: 16px;
          }
          
          .share-modal-button-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          
          .share-modal-header,
          .share-modal-textarea-container,
          .share-modal-buttons-container {
            padding-left: 16px;
            padding-right: 16px;
          }
          
          .share-modal-title {
            font-size: 18px;
          }
          
          .share-modal-textarea {
            min-height: 90px;
            padding: 12px;
          }
          
          .share-modal-btn {
            height: 42px;
          }
        }
      `}</style>

      <div className="share-modal-backdrop" onClick={onClose}>
        <div className="share-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header mit VersusArena-Branding */}
          <div className="share-modal-header">
            <h3 className="share-modal-title">🎮 Share Tournament</h3>
            <p className="share-modal-subtitle">Share this tournament with your gaming friends!</p>
          </div>

          {/* Verbessertes Textfeld */}
          <div className="share-modal-textarea-container">
            <label className="share-modal-label">Share Text:</label>
            <textarea 
              ref={textareaRef}
              readOnly 
              value={enhancedMessage} 
              className="share-modal-textarea"
              onClick={handleTextareaClick}
              placeholder="Tournament information will appear here..."
            />
            <div className="share-modal-textarea-hint">💡 Click to auto-select text</div>
          </div>

          {/* Social Media Buttons */}
          <div className="share-modal-buttons-container">
            <div className="share-modal-button-grid">
              <button className="share-modal-btn share-modal-btn-copy" onClick={copyText}>
                <span className="share-modal-btn-icon">📄</span>
                <span className="share-modal-btn-text">Copy</span>
              </button>
              <button className="share-modal-btn share-modal-btn-x" onClick={shareX}>
                <span className="share-modal-btn-icon">𝕏</span>
                <span className="share-modal-btn-text">X / Twitter</span>
              </button>
              <button className="share-modal-btn share-modal-btn-whatsapp" onClick={shareWhatsApp}>
                <span className="share-modal-btn-icon">📞</span>
                <span className="share-modal-btn-text">WhatsApp</span>
              </button>
              <button className="share-modal-btn share-modal-btn-telegram" onClick={shareTelegram}>
                <span className="share-modal-btn-icon">📤</span>
                <span className="share-modal-btn-text">Telegram</span>
              </button>
              <button className="share-modal-btn share-modal-btn-sms" onClick={shareSMS}>
                <span className="share-modal-btn-icon">📱</span>
                <span className="share-modal-btn-text">SMS</span>
              </button>
              <button className="share-modal-btn share-modal-btn-more" onClick={shareMore}>
                <span className="share-modal-btn-icon">⋯</span>
                <span className="share-modal-btn-text">More...</span>
              </button>
            </div>
          </div>

          {/* Close Button */}
          <div className="share-modal-footer">
            <button className="share-modal-btn-close" onClick={onClose}>
              <span className="share-modal-btn-icon">✕</span>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Styles wurden durch CSS-Klassen ersetzt - siehe inline <style> Tag oben
